import { calculateStartsAndEndsAtDate } from '@app/src/components/CreateNewRequestModal/utils'
import FilterDisplayChip from '@app/src/components/FilterDisplayChip'
import StyledChip from '@app/src/components/StyledChip'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import { InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Stack, Typography } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { AutomationFormData } from '../../CreateAutomationModal'
import { RequestAutomationType } from '../../RequestAutomationType'

const summaryLabelKeyMap: Partial<Record<RequestAutomationType, string>> = {
  [RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL]:
    'form.createAutomation.automationType.sendQuestionnaireIf',
  [RequestAutomationType.FOR_EVERY_NEW_PROVIDER]: 'form.createAutomation.automationType.sendQuestionnaireIf',
}

const SummarySection: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <Stack spacing={2}>
    <Typography variant="subtitle1">{label}</Typography>
    <Box display="flex" columnGap={1}>
      {children}
    </Box>
  </Stack>
)

export const RequestAutomationSummary = ({
  filters,
  translationKey,
}: {
  filters: FilterGroup[]
  translationKey: string
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { getValues } = useFormContext<AutomationFormData>()
  const { getPeriodDisplayName } = usePeriodName()

  const data = getValues()
  const templateNames = data.templates.map(t => t.title)
  const { periodEndsAt, periodStartsAt } = calculateStartsAndEndsAtDate({
    dateType: data.dateType,
    datePeriod: data.datePeriod,
    dateYear: data.dateYear,
  })
  const periodDisplayName = getPeriodDisplayName(periodStartsAt, periodEndsAt)

  const automationType = data.automationType

  const renderChipsByAutomationType: Partial<Record<RequestAutomationType, () => React.ReactNode>> = {
    [RequestAutomationType.FOR_EVERY_NEW_PROVIDER]: () => (
      <StyledChip label={formatMessage({ id: 'form.createAutomation.automationType.newCompany' })} />
    ),
  }

  return (
    <Stack spacing={3}>
      <SummarySection label={formatMessage({ id: summaryLabelKeyMap[automationType] })}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {renderChipsByAutomationType[automationType]
            ? renderChipsByAutomationType[automationType]?.()
            : filters.map(filter =>
                filter.filters[0] ? (
                  <FilterDisplayChip
                    key={`${filter.name}-${filter.filters[0].value}`}
                    filterName={filter.name}
                    filterValue={filter.filters[0].value}
                    operator={filter.filters[0].operator}
                  />
                ) : null,
              )}
        </Box>
      </SummarySection>
      <SummarySection label={formatMessage({ id: 'form.createAutomation.automationType.questionnaires' })}>
        {templateNames.map(template => (
          <StyledChip key={template} label={template} />
        ))}
      </SummarySection>
      <SummarySection label={formatMessage({ id: 'form.createAutomation.automationType.period' })}>
        <StyledChip label={periodDisplayName} />
      </SummarySection>

      {((data.deadlineOffsetType && data.deadlineOffsetType !== 'custom') || data.customDueDate) && (
        <>
          <SummarySection label={formatMessage({ id: 'reporting.deadline' })}>
            <StyledChip
              label={
                data.customDueDate
                  ? formatDate(getDateWithoutTimeFromString(data.customDueDate as string), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : formatMessage({ id: translationKey }, { days: data.deadlineOffsetType })
              }
            />
          </SummarySection>

          {data.customDueDate && (
            <Alert severity="info" icon={<InfoOutlined />}>
              {formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateWarning' })}
            </Alert>
          )}
        </>
      )}
    </Stack>
  )
}
